import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SponsorDoc } from "../../types/sponsors";
import { FormattedMessage, IntlProvider } from "react-intl";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { messages, SupportedLanguages } from "../../languages/languages";
import { CustomTextField } from "../../components/custom-textfield";
import {
  CampaignDoc,
  Candidate,
  CandidateDoc,
  CandidateErrors,
  EMPTY_CANDIDATE_DOC,
  EMPTY_CANDIDATE_ERRORS,
  RegistrationInfo,
  VERIFICATION_EXPIRY_HOURS,
} from "../../types/recruiting";
import {
  isEMailValid,
  isNameValid,
  isPhoneValid,
  urlAppendCacheBuster,
} from "../../utils/utils";
import {
  Circle,
  CircleOutlined,
  WarningAmber as WarningIcon,
  UploadFile,
} from "@mui/icons-material";
import { RadioYesNo } from "../../components/radio-button-yes-no";
import { SelectCountryTranslated } from "../../components/select-country";
import {
  checkAndUpdateDuplicatePassports,
  generateConfirmationCode,
  loadCampaign,
  loadCandidates,
  saveCandidate,
  storeRegistrationInfo,
} from "../../data-functions/recruiting_api";
import { loadRegions, loadSponsor } from "../../data-functions/system-data_api";
import emailSpellChecker from "@zootools/email-spell-checker";
import { v4 as uuidv4 } from "uuid";

import {
  calcScore,
  calculateSurveyScore,
  sendCandidateEmail,
} from "../../utils/candidate-utils";
import {
  ADVANCED_FORM_QUESTIONS,
  KYRGYZ_QUESTIONS,
  RUSSIAN_QUESTIONS,
  UNIVERSITY_QUESTIONS,
} from "./interview-questions";
import QuestionComponent from "../../components/recruiting/question-component";
import { DISQUALIFY_NUM_VALUE, Question } from "../../types/surveys";
import { Regions } from "../../types/commons";
import { getDownloadURL, ref, uploadString } from "firebase/storage";
import { BASE_FUNCTIONS_URL, storage } from "../../types/firebase";
import type { User } from "firebase/auth";
import { LoadingButton } from "@mui/lab";
import { AuthContext } from "../../components/auth-provider";
import { getServerTime } from "../../data-functions/common-api";

declare global {
  interface Window {
    mrz_worker: any;
  }
}

type ParamTypes = {
  cid: string;
};

const register_disclaimer_text =
  "Important: Registering your interest for the SWS and placements is free. " +
  "You are not required to pay for or do anything against your will to apply for or be offered a SWS placement.";

const ACTIVE_STEP_EMPTY = -1;
const ACTIVE_STEP_REGISTER_FORM = 0;
const ACTIVE_STEP_FINISHED = 1;
const ACTIVE_STEP_INVALID_CAMPAIGN = 2;
const ACTIVE_STEP_EXPIRED_CAMPAIGN = 3;
// const ACTIVE_STEP_SCAN_PASSPORT = 4;
const ACTIVE_STEP_WELCOME = 5;
const ACTIVE_STEP_DISCLAIMER = 6;
const ACTIVE_STEP_GOOGLE = 7;
const ACTIVE_STEP_ADVANCED_FORM = 8;
const ACTIVE_STEP_UPLOAD_PASSPORT = 9;

export const RegisterCandidate = () => {
  const { currentUser } = useContext(AuthContext)!;

  const { cid } = useParams<ParamTypes>();
  const [campaign, setCampaign] = useState<CampaignDoc | null>(null);
  const [sponsor, setSponsor] = useState<SponsorDoc | null>(null);
  const [language, setLanguage] = useState<SupportedLanguages>("en");
  const [candidate, setCandidate] = useState<CandidateDoc>({
    ...EMPTY_CANDIDATE_DOC,
  });
  const [regions, setRegions] = useState<Regions | null>(null);
  const [activeStep, setActiveStep] = useState<number>(ACTIVE_STEP_EMPTY);
  const [errors, setErrors] = useState<CandidateErrors>({
    ...EMPTY_CANDIDATE_ERRORS,
  });
  const [emailSuggestion, setEmailSuggestion] = useState<string>("");
  const [registrationInfo, setRegistrationInfo] =
    useState<RegistrationInfo | null>(null);
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});

  const [isKyrgyzstanOperator, setIsKyrgyzstanOperator] =
    useState<boolean>(false);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageFileExtension, setImageFileExtension] = useState<string>("");
  const [kyrgyzPhotoSrc, setKyrgyzPhotoSrc] = useState<string | null>(null);
  const [kyrgyzPhotoExtension, setKyrgyzPhotoExtension] = useState<string>("");
  const [kyrgyzPassportSrc, setKyrgyzPassportSrc] = useState<string | null>(
    null,
  );
  const [kyrgyzPassportExtension, setKyrgyzPassportExtension] =
    useState<string>("");
  const [kyrgyzReceiptSrc, setKyrgyzReceiptSrc] = useState<string | null>(null);
  const [kyrgyzReceiptExtension, setKyrgyzReceiptExtension] =
    useState<string>("");
  const [isGoogleVerified, setIsGoogleVerified] = useState<boolean>(false);
  const [googleVerificationError, setGoogleVerificationError] =
    useState<string>("");
  const [googleUser, setGoogleUser] = useState<User | null>(null);

  const [gisReady, setGisReady] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement | null>(null);

  const registerInProgress = useRef<boolean>(false);
  const ipFetchInitiated = useRef(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isGoogleVerificationCampaign =
    campaign?.campaign.verificationType === "google";

  useEffect(() => {
    // wait for GIS to load
    const interval = setInterval(() => {
      // @ts-ignore
      if (window.google?.accounts?.id) {
        setGisReady(true);
        clearInterval(interval);
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchCampaignData = async () => {
      if (cid) {
        const campaignDoc = await loadCampaign(cid);
        let newCampaign;
        if (campaignDoc) {
          setCampaign(campaignDoc);
          newCampaign = campaignDoc.campaign;
        } else {
          setCampaign(null);
          console.error("Campaign ID: " + cid + " was not found");
          setActiveStep(ACTIVE_STEP_INVALID_CAMPAIGN);
          return;
        }

        const sponsor = await loadSponsor(newCampaign.sponsorId);
        setIsKyrgyzstanOperator(
          sponsor?.sponsor.address?.country.code === "KG" ||
            sponsor?.sponsor.address?.country.label?.trim().toLowerCase() ===
              "kyrgyzstan",
        );

        if (sponsor) {
          setSponsor(sponsor);
        }

        if (newCampaign.language && newCampaign.language !== language) {
          setLanguage(newCampaign.language as SupportedLanguages);
        }

        if (
          newCampaign.status === "open" ||
          (newCampaign.status === "new" && currentUser?.appUser)
        ) {
          setActiveStep(ACTIVE_STEP_WELCOME);
        } else {
          setActiveStep(ACTIVE_STEP_EXPIRED_CAMPAIGN);
        }

        if (newCampaign.includeRegion && newCampaign.country) {
          const regions = await loadRegions(newCampaign.country);
          setRegions(regions);
        }
      } else {
        console.error("Campaign ID: " + cid + " was not found");
        setActiveStep(ACTIVE_STEP_INVALID_CAMPAIGN);
      }
    };

    const fetchIpAddress = async () => {
      fetch("https://api4.ipify.org?format=json")
        .then(
          (res) => res.json(),
          (err) => console.error("Error fetching IP address:", err),
        )
        .then(
          (data) => {
            let deviceId = localStorage.getItem("gwf_deviceId");
            if (!deviceId) {
              deviceId = uuidv4();
              localStorage.setItem("gwf_deviceId", deviceId);
            }

            const rInfo: RegistrationInfo = {
              sponsorId: sponsor?.id || "",
              candidateId: "",
              campaignId: "",
              browserAgent: navigator.userAgent,
              ip4Address: data.ip,
              ip6Address: "",
              deviceId: deviceId,
              createdAt: new Date().toISOString(),
            };
            setRegistrationInfo(rInfo);
          },
          (err) => console.error("Error fetching IP address:", err),
        );
    };

    if (!ipFetchInitiated.current) {
      ipFetchInitiated.current = true;
      fetchIpAddress();
    }

    fetchCampaignData();
    setEmailSuggestion("");
  }, [cid]);

  useEffect(() => {
    if (!isGoogleVerificationCampaign) {
      setGoogleUser(null);
      setIsGoogleVerified(false);
      setGoogleVerificationError("");
      return;
    }
  }, [isGoogleVerificationCampaign]);

  useEffect(() => {
    // console.log("gisReady", gisReady);
    // console.log("googleBtnRef.current", googleBtnRef.current);
    if (
      !gisReady ||
      !isGoogleVerificationCampaign ||
      activeStep !== ACTIVE_STEP_GOOGLE ||
      !googleBtnRef.current
    ) {
      return;
    }

    // @ts-ignore
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
      callback: async (response: any) => {
        const idToken = response?.credential;
        if (!idToken) return;

        const resp = await fetch(`${BASE_FUNCTIONS_URL}/public/google/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_FUNCTIONS_API_KEY!,
          },
          body: JSON.stringify({ idToken }),
        });

        const json = await resp.json();
        if (json?.data?.status === "OK") {
          const v = json.data.verification;
          // mark as verified and hydrate name/email
          setIsGoogleVerified(true);
          setCandidate((prev) => {
            const updated = { ...prev.candidate };
            updated.email = v.email;
            if (!updated.name && v.given_name)
              updated.name = v.given_name.toUpperCase();
            if (!updated.surname && v.family_name)
              updated.surname = v.family_name.toUpperCase();
            // store provider metadata like you already do at save time
            return { ...prev, candidate: updated };
          });
          setGoogleUser(v);
          setGoogleVerificationError("");
        } else {
          setIsGoogleVerified(false);
          setGoogleVerificationError(
            "Google verification failed. Please try again.",
          );
        }
      },
    });

    // Render the official Google button
    // @ts-ignore
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      type: "standard",
      text: "signin_with",
      shape: "rectangular",
    });

    return () => {
      // @ts-ignore
      window.google.accounts.id.cancel?.();
      googleBtnRef.current?.replaceChildren();
    };
  }, [gisReady, isGoogleVerificationCampaign, activeStep]);

  // const handleLoadPassport = async () => {
  // Create an input element to select a file
  // const input = document.createElement("input");
  // input.type = "file";
  // input.accept = "image/*";
  // // Add an event listener to handle the file selection
  // input.onchange = async (event) => {
  //   const file = (event.target as HTMLInputElement).files?.[0];
  //   if (file) {
  //     try {
  //       const reader = new FileReader();
  //       reader.onload = function (e: ProgressEvent<FileReader>) {
  //         console.log("reader onload", e.target);
  //         console.log("worker", worker);
  //         if (e.target && e.target.result && worker) {
  //           console.log("posting message");
  //           worker.postMessage({
  //             cmd: "process",
  //             image: e.target.result,
  //           });
  //         }
  //       };
  //       console.log("reading file");
  //       reader.readAsDataURL(file);
  //     } catch (error) {
  //       console.error("Error scanning passport:", error);
  //     }
  //   }
  // };
  // // Trigger the file input click event
  // input.click();
  // };

  const getQuestionValue = (qid: string) => {
    const answer = candidate?.candidate?.surveyAnswers?.[qid];

    return answer === undefined ? undefined : answer;
  };

  const isJpegFile = (file: File) => {
    const fileName = file.name.toLowerCase();
    return (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg")
    );
  };

  const isKyrgyzBirthDateValid = (birthDate: string | undefined): boolean => {
    if (!birthDate) return false;
    return /^\d{4}([/-])\d{2}\1\d{2}$/.test(birthDate);
  };

  const handleJpegUpload = (
    file: File | undefined,
    setImage: React.Dispatch<React.SetStateAction<string | null>>,
    setExtension: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (!file) {
      return;
    }

    if (!isJpegFile(file)) {
      alert("Please upload only JPEG/JPG images");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImage(result);
      setExtension((file.name.split(".").pop() || "jpg").toLowerCase());
    };
    reader.readAsDataURL(file);
  };

  const handleAnswerChange = (
    event: React.ChangeEvent<HTMLInputElement> | any,
    question?: Question,
    value?: any,
  ) => {
    if (candidate?.candidate && question) {
      const key = question.id;
      let newInterviewAnswers = { ...candidate.candidate.surveyAnswers };
      if (!newInterviewAnswers) {
        newInterviewAnswers = {};
      }

      newInterviewAnswers[key] = value;
      setCandidate({
        ...candidate,
        candidate: {
          ...candidate.candidate,
          surveyAnswers: newInterviewAnswers,
        },
      });

      let score = calculateSurveyScore(
        newInterviewAnswers,
        ADVANCED_FORM_QUESTIONS,
        campaign?.campaign.surveyQuestionScores,
      );
      if (
        campaign?.campaign.language === "ru" &&
        score > DISQUALIFY_NUM_VALUE
      ) {
        const sc = calculateSurveyScore(
          newInterviewAnswers,
          RUSSIAN_QUESTIONS,
          campaign?.campaign.surveyQuestionScores,
        );
        if (sc > DISQUALIFY_NUM_VALUE) {
          score += sc;
        } else {
          score = DISQUALIFY_NUM_VALUE;
        }
      }
    }
  };

  const handleUploadCandidateImage = async (
    imageId: string,
    imageData: string | null,
    extension: string,
    suffix: string,
  ) => {
    let downloadURL: string = "";

    try {
      const imageName = imageId ? imageId : uuidv4();
      const fileExtension = extension || "jpg";
      const fileName = `candidates/${campaign?.id}/${imageName}_${suffix}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      // Define metadata with Cache-Control
      const metadata = {
        cacheControl: "public, max-age=31536000", // Example: 1 year
      };

      await uploadString(storageRef, imageData || "", "data_url", metadata);

      // Step 3: Get download URL
      downloadURL = await getDownloadURL(storageRef);
      return urlAppendCacheBuster(downloadURL);
    } catch (err) {
      console.error("Error uploading passport image:", err);
    } finally {
      return downloadURL;
    }
  };

  const handleUploadPassportImage = async (imageId: string) => {
    return handleUploadCandidateImage(
      imageId,
      imageSrc,
      imageFileExtension,
      "passport",
    );
  };

  const validateInput = async (candidate: Candidate) => {
    let newErrors: CandidateErrors = { ...EMPTY_CANDIDATE_ERRORS };
    let newAnswerErrors: Record<string, string> = {};
    let formIsValid = true;

    if (!(candidate.name && isNameValid(candidate.name))) {
      newErrors.name =
        "Name is required and should only contain alphabetic characters";
      formIsValid = false;
    }

    if (!(candidate.surname && isNameValid(candidate.surname))) {
      newErrors.surname =
        "Surname is required and should only contain alphabetic characters";
      formIsValid = false;
    }

    if (!candidate.gender) {
      newErrors.gender = "Gender is required";
      formIsValid = false;
    }

    if (
      isKyrgyzstanOperator &&
      (candidate.phone?.startsWith("0") || candidate.phone?.startsWith("+"))
    ) {
      newErrors.phone =
        "Phone number should start without 0 or + (use country code only).";
      console.log("newErrors", newErrors);
      formIsValid = false;
    } else if (!isPhoneValid(candidate.phone)) {
      newErrors.phone =
        "Phone number is required and should only contain numbers!";
      formIsValid = false;
    }

    if (!candidate.privacy_policy_accepted) {
      formIsValid = false;
      newErrors.privacy_policy_accepted =
        "Please, accept the privacy policy, so that we can continue with your application!";
    }

    if (!candidate.personal_data_processing) {
      formIsValid = false;
      newErrors.personal_data_processing =
        "Please, consent to the processing of your personal data, so that we can continue with your application!";
    }

    console.log("candidate.birth_date: ", candidate.birth_date);
    if (!candidate.birth_date) {
      console.log("newErrors", newErrors);
      formIsValid = false;
      newErrors.birth_date = "Please, enter your date of birth";
    } else if (
      isKyrgyzstanOperator &&
      !isKyrgyzBirthDateValid(candidate.birth_date)
    ) {
      formIsValid = false;
      newErrors.birth_date = "Please use YYYY/MM/DD format";
      console.log("newErrors", newErrors);
    }

    if (candidate.email && isEMailValid(candidate.email)) {
      if (campaign) {
        if (campaign.campaign.restrictEmailProvider) {
          const mailProvider = candidate.email.split("@")[1];
          if (mailProvider !== campaign.campaign.emailProviderUrl) {
            newErrors.email =
              "Please, use the email address from the email provider " +
              campaign.campaign.emailProviderUrl;
            newErrors.emailDomain = newErrors.email;
            formIsValid = false;
          }
        }
        if (formIsValid) {
          const duplCandidates = await loadCandidates(null, campaign.id, {
            email: { value: candidate.email.trim(), operator: "==" },
          });

          const candidateFound = Object.keys(duplCandidates).length > 0;

          if (candidateFound) {
            newErrors.email =
              "A candidate with this email address already exists";
            formIsValid = false;
          }
        }
      }
    } else {
      newErrors.email = "Please enter a valid email address";
      console.log("newErrors", newErrors);
      formIsValid = false;
    }

    if (
      candidate.has_passport &&
      (!candidate.passportId || candidate.passportId.length <= 3)
    ) {
      newErrors.passportId = "Please, enter a valid passport number";
      console.log("newErrors", newErrors);
      formIsValid = false;
    }

    if (campaign?.campaign.includeRegion && !candidate.region) {
      newErrors.region = "Please, select a region";
      console.log("newErrors", newErrors);
      formIsValid = false;
    }
    if (campaign?.campaign.includeUniversity && !candidate.university) {
      newErrors.university = "Please, select a university";
      formIsValid = false;
    }
    // Validate companion information if not traveling alone
    const companionSelected =
      campaign?.campaign.askForSpouseApplication &&
      (candidate.travelCompanion === "spouse" ||
        candidate.travelCompanion === "relative_friend");

    console.log("companionSelected", companionSelected);
    if (companionSelected) {
      if (!(candidate.spouseName && isNameValid(candidate.spouseName))) {
        console.log("newErrors", newErrors);
        newErrors.spouseName =
          "Spouse name is required and should only contain alphabetic characters";
        formIsValid = false;
      }

      if (!(candidate.spouseSurname && isNameValid(candidate.spouseSurname))) {
        newErrors.spouseSurname =
          "Spouse surname is required and should only contain alphabetic characters";
        formIsValid = false;
      }
      if (!candidate.spouseEmail || !isEMailValid(candidate.spouseEmail)) {
        newErrors.spouseEmail =
          "Please enter a valid email address for your companion";
        formIsValid = false;
      } else if (
        candidate.email &&
        candidate.spouseEmail.toLowerCase() === candidate.email.toLowerCase()
      ) {
        newErrors.spouseEmail =
          "Companion email must be different from your email address";
        formIsValid = false;
      }
    }

    // Additional validation for relative/friend
    if (
      campaign?.campaign.askForSpouseApplication &&
      candidate.travelCompanion === "relative_friend"
    ) {
      if (!candidate.companionGender) {
        newErrors.companionGender = "Please select the companion's gender";
        console.log("newErrors", newErrors);
        formIsValid = false;
      }
      if (!candidate.companionRelationType) {
        newErrors.companionRelationType =
          "Please select the companion's relationship type";
        formIsValid = false;
      }
    }

    if (campaign?.campaign.showCurrentLocation) {
      if (!candidate.currentLocation || !candidate.currentLocation.code) {
        newErrors.currentLocation = "Please select your current location";
        formIsValid = false;
      }
      if (candidate.canAttendInterview === undefined) {
        newErrors.canAttendInterview = "Please answer this question";
        formIsValid = false;
      }
    }

    console.log("activeStep", activeStep);
    console.log("formIsValid", formIsValid);

    if (activeStep === ACTIVE_STEP_ADVANCED_FORM) {
      if (isKyrgyzstanOperator) {
        const kyrgyzQuestions = KYRGYZ_QUESTIONS.filter(
          (question) => question.required,
        );
        for (const question of kyrgyzQuestions) {
          const value = getQuestionValue(question.id);
          if (!value) {
            newAnswerErrors[question.id] = "This field is required";
            formIsValid = false;
          }
        }
      } else {
        const advancedFormQuestions = ADVANCED_FORM_QUESTIONS.filter(
          (question) => question.required,
        );
        for (const question of advancedFormQuestions) {
          const value = getQuestionValue(question.id);

          if (!value) {
            newAnswerErrors[question.id] = "This field is required";
            formIsValid = false;
          }
        }

        if (campaign?.campaign.language === "ru") {
          const russianQuestions = RUSSIAN_QUESTIONS.filter(
            (question) => question.required,
          );
          for (const question of russianQuestions) {
            const value = getQuestionValue(question.id);
            if (!value) {
              newAnswerErrors[question.id] = "This field is required";
              formIsValid = false;
            }
          }
        }
      }
    }

    setErrors(newErrors);
    setAnswerErrors(newAnswerErrors);
    return formIsValid;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.name;

    let newCandidate;

    if (key === "privacy_policy") {
      newCandidate = {
        ...candidate,
        candidate: {
          ...candidate.candidate,
          privacy_policy_accepted: event.target.checked,
        },
      };
    } else if (key === "personal_data_processing") {
      newCandidate = {
        ...candidate,
        candidate: {
          ...candidate.candidate,
          personal_data_processing: event.target.checked,
        },
      };
    } else if (
      key === "has_passport" ||
      key === "applyingWithSpouse" ||
      key === "travelIfSpouseUnsuccessful" ||
      key === "canAttendInterview"
    ) {
      newCandidate = {
        ...candidate,
        candidate: {
          ...candidate.candidate,
          [key]: event.target.value === "true",
        },
      };
    } else if (key === "travelCompanion") {
      const value = event.target.value;
      newCandidate = {
        ...candidate,
        candidate: {
          ...candidate.candidate,
          travelCompanion: value,
          applyingWithSpouse: value === "spouse",
        },
      };
    } else if (key === "passportId") {
      const val = event.target.value.toUpperCase().replace(/\s/g, "");
      newCandidate = {
        ...candidate,
        candidate: { ...candidate.candidate, passportId: val },
      };
      newCandidate.candidate.has_passport = val.length > 0;
    } else if (
      key === "name" ||
      key === "surname" ||
      key === "spouseName" ||
      key === "spouseSurname"
    ) {
      newCandidate = {
        ...candidate,
        candidate: {
          ...candidate.candidate,
          [key]: event.target.value.toUpperCase(),
        },
      };
    } else if (key === "birth_date" && isKyrgyzstanOperator) {
      const normalizedValue = event.target.value;
      newCandidate = {
        ...candidate,
        candidate: { ...candidate.candidate, [key]: normalizedValue },
      };
    } else {
      newCandidate = {
        ...candidate,
        candidate: { ...candidate.candidate, [key]: event.target.value },
      };
    }

    setCandidate(newCandidate);
  };

  const handleRegionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCandidate = { ...candidate };
    const region = regions?.regions.find((r) => r.code === event.target.value);
    if (region) {
      newCandidate.candidate.region = region;
    } else {
      if (newCandidate.candidate.region !== undefined) {
        delete newCandidate.candidate.region;
      }
    }
    setCandidate(newCandidate);
    console.log("newCandidate: ", newCandidate);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Your logic here
    console.log("Input field lost focus:", event.target.value);
    const email = event.target.value;
    if (email) {
      const suggestedEmail = emailSpellChecker.run({
        email,
      });
      if (suggestedEmail) {
        // Make the warning container visible,

        // and add warning text
        setEmailSuggestion(suggestedEmail.full);
      } else {
        // Else, hide the warning container
        setEmailSuggestion("");
      }
    }
  };

  const handleBirthDateBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!isKyrgyzstanOperator) return;
    const value = event.target.value;
    setErrors((prev) => ({
      ...prev,
      birth_date:
        value && isKyrgyzBirthDateValid(value)
          ? ""
          : "Please use YYYY/MM/DD format",
    }));
  };

  const handleSave = async () => {
    if (registerInProgress.current === true) {
      console.log("registering in progress");
      return;
    }

    console.log("handleSave");

    registerInProgress.current = true;
    setGoogleVerificationError("");

    const newCandidate = { ...candidate.candidate };
    newCandidate.name = newCandidate.name.trim();
    newCandidate.surname = newCandidate.surname.trim();
    newCandidate.email = newCandidate.email.toLowerCase().trim();
    newCandidate.phone = newCandidate.phone.trim();
    if (isKyrgyzstanOperator && newCandidate.birth_date) {
      newCandidate.birth_date = newCandidate.birth_date.replace(/-/g, "/");
    }
    if (newCandidate.passportId) {
      newCandidate.passportId = newCandidate.passportId.trim();
    }
    newCandidate.preferredLanguage = language;
    newCandidate.sponsorId = sponsor ? sponsor.id : "";

    const isValid = await validateInput(newCandidate);
    console.log("isValid", isValid);
    if (!isValid) {
      registerInProgress.current = false;
      return;
    }

    if (
      activeStep === ACTIVE_STEP_UPLOAD_PASSPORT &&
      isKyrgyzstanOperator &&
      (!kyrgyzPhotoSrc || !kyrgyzPassportSrc || !kyrgyzReceiptSrc)
    ) {
      alert("Please upload all 3 required JPEG files.");
      registerInProgress.current = false;
      return;
    }

    if (campaign && campaign.id) {
      const verificationType = campaign.campaign.verificationType || "email";
      const expiryHours =
        (campaign.campaign.verificationExpiryHours ||
          VERIFICATION_EXPIRY_HOURS) *
        60 *
        60 *
        1000;
      newCandidate.campaignId = campaign.id;
      newCandidate.agentId = campaign.campaign.agentId || "";
      newCandidate.verification = {
        verificationCode: uuidv4(),
        expiryDate: new Date(Date.now() + expiryHours).toISOString(),
      };

      if (newCandidate.surveyAnswers && campaign?.campaign) {
        const score = await calcScore(null, newCandidate, campaign);
        newCandidate.surveyScore = score;
        if (score === DISQUALIFY_NUM_VALUE) {
          newCandidate.status = "disqualified";
        }
      }

      if (
        campaign?.campaign.showCurrentLocation &&
        newCandidate.canAttendInterview === false
      ) {
        newCandidate.status = "disqualified";
        newCandidate.surveyScore = DISQUALIFY_NUM_VALUE;
      }

      const serverTime = await getServerTime();
      const confirmationCode = await generateConfirmationCode(
        campaign.id || "",
      );
      newCandidate.verification.confirmationCode = confirmationCode;

      if (!newCandidate.verification) {
        newCandidate.verification = {
          verificationCode: "",
          expiryDate: serverTime.iso,
        };
      }

      if (verificationType === "automatic") {
        newCandidate.verification.verifiedOn = serverTime.iso;
        newCandidate.status = campaign?.campaign?.inviteAutomatically
          ? "invited"
          : "verified";
      } else if (verificationType === "google") {
        console.log("verificationType === google");
        console.log("googleUser", googleUser);
        if (!googleUser) {
          setGoogleVerificationError(
            "Google authentication is required to complete registration.",
          );
          registerInProgress.current = false;
          return;
        }

        newCandidate.verification.verifiedOn = serverTime.iso;
        newCandidate.verification.method = "google";
        newCandidate.verification.provider = "google.com";
        newCandidate.verification.providerUid = googleUser.uid;
        newCandidate.verification.providerEmail = googleUser.email || "";
        newCandidate.verification.providerDisplayName =
          googleUser.displayName || "";
        newCandidate.status = campaign?.campaign?.inviteAutomatically
          ? "invited"
          : "verified";
      }

      if (newCandidate.passportId && campaign?.id) {
        const duplicateCount = await checkAndUpdateDuplicatePassports(
          campaign.id,
          newCandidate.passportId,
          newCandidate.email,
        );
        if (duplicateCount > 0) {
          newCandidate.duplicatePassportCount = duplicateCount;
        }
      }

      let candidateId = "";
      try {
        const candDoc = await saveCandidate(
          null,
          { id: "", candidate: newCandidate },
          true,
        );
        console.log("saved candDoc: ", candDoc);
        if (candDoc) {
          candidateId = candDoc.id;
          if (registrationInfo) {
            registrationInfo.candidateId = candDoc.id;
            registrationInfo.campaignId = campaign.id;
            registrationInfo.sponsorId = sponsor ? sponsor.id : "";
            console.log("storing registration info", registrationInfo);
            try {
              const regInfoDoc = await storeRegistrationInfo(
                null,
                { id: "", registrationInfo },
                true,
              );
              if (regInfoDoc && regInfoDoc.id) {
                console.log("registration info stored", regInfoDoc);
              } else {
                console.log("registration info not stored");
              }
            } catch (err) {
              console.error("Error storing registration info:", err);
            }
          } else {
            console.log("no registration info to store");
          }

          const templateId = campaign.campaign.inviteAutomatically
            ? "candidate_verification"
            : "candidate_verification_no_invitation";
          try {
            if (verificationType === "email") {
              await sendCandidateEmail([candDoc.id], templateId);
            }
            // if the verification is google or automatic and
            // invite is automatically, send confirmation code
            else if (campaign.campaign.inviteAutomatically) {
              await sendCandidateEmail([candDoc.id], "candidate_confirmation");
            }
          } catch (err) {
            console.error("Error sending email verification email:", err);
          }
          setCandidate(candDoc);
        }
      } catch (err) {
        registerInProgress.current = false;
        console.error(err);
        return;
      }

      if (candidateId) {
        if (isKyrgyzstanOperator) {
          try {
            const [
              kyrgyzPhotoImageUrl,
              kyrgyzPassportImageUrl,
              kyrgyzReceiptUrl,
            ] = await Promise.all([
              handleUploadCandidateImage(
                candidateId,
                kyrgyzPhotoSrc,
                kyrgyzPhotoExtension,
                "kyrgyz_photo",
              ),
              handleUploadCandidateImage(
                candidateId,
                kyrgyzPassportSrc,
                kyrgyzPassportExtension,
                "kyrgyz_passport",
              ),
              handleUploadCandidateImage(
                candidateId,
                kyrgyzReceiptSrc,
                kyrgyzReceiptExtension,
                "kyrgyz_receipt",
              ),
            ]);

            const candidateWithKyrgyzImages: Candidate = {
              ...newCandidate,
              imageUrl: kyrgyzPassportImageUrl,
              passportImageUrl: kyrgyzPassportImageUrl,
            };
            Object.assign(candidateWithKyrgyzImages, {
              kyrgyzPhotoImageUrl,
              kyrgyzPassportImageUrl,
              kyrgyzPaymentReceiptImageUrl: kyrgyzReceiptUrl,
            });

            await saveCandidate(
              null,
              {
                id: candidateId,
                candidate: candidateWithKyrgyzImages,
              },
              false,
            );
          } catch (err) {
            console.error("Error uploading Kyrgyz candidate images:", err);
          } finally {
            setKyrgyzPhotoSrc(null);
            setKyrgyzPhotoExtension("");
            setKyrgyzPassportSrc(null);
            setKyrgyzPassportExtension("");
            setKyrgyzReceiptSrc(null);
            setKyrgyzReceiptExtension("");
          }
        } else if (campaign?.campaign.requirePassportScan && imageSrc) {
          console.log("uploading passport image");
          try {
            const imageUrl = await handleUploadPassportImage(candidateId);
            console.log("image URL from handleUploadPassportImage: ", imageUrl);
            if (imageUrl) {
              await saveCandidate(
                null,
                { id: candidateId, candidate: { ...newCandidate, imageUrl } },
                false,
              );
            }
          } catch (err) {
            console.error("Error uploading passport image:", err);
          } finally {
            setImageSrc(null);
          }
        }
      }

      console.log("isGoogleVerificationCampaign", isGoogleVerificationCampaign);
      setActiveStep(ACTIVE_STEP_FINISHED);
    }

    registerInProgress.current = false;
  };

  const handleNext = async () => {
    let isValid = true;
    let newErrors: CandidateErrors = { ...EMPTY_CANDIDATE_ERRORS };
    if (!candidate.candidate.privacy_policy_accepted) {
      newErrors.privacy_policy_accepted =
        "Please, accept the privacy policy, so that we can continue with your application!";
      isValid = false;
    }

    if (!candidate.candidate.personal_data_processing) {
      newErrors.personal_data_processing =
        "Please, consent to the processing of your personal data, so that we can continue with your application!";
      isValid = false;
    }

    if (!isValid) {
      console.log("newErrors", newErrors);
      setErrors(newErrors);
      return;
    }

    switch (activeStep) {
      case ACTIVE_STEP_WELCOME:
        if (campaign && campaign.campaign.showPaymentsDisclaimer) {
          setActiveStep(ACTIVE_STEP_DISCLAIMER);
        } else if (isGoogleVerificationCampaign) {
          setActiveStep(ACTIVE_STEP_GOOGLE);
        } else {
          setActiveStep(ACTIVE_STEP_REGISTER_FORM);
        }
        break;
      case ACTIVE_STEP_DISCLAIMER:
        if (isGoogleVerificationCampaign) {
          setActiveStep(ACTIVE_STEP_GOOGLE);
        } else {
          setActiveStep(ACTIVE_STEP_REGISTER_FORM);
        }
        break;
      case ACTIVE_STEP_GOOGLE:
        if (!isGoogleVerified) {
          setGoogleVerificationError(
            "Please sign in with Google before continuing with the registration.",
          );
          return;
        }
        setActiveStep(ACTIVE_STEP_REGISTER_FORM);
        break;
      case ACTIVE_STEP_REGISTER_FORM:
        {
          const isValid = await validateInput(candidate.candidate);
          if (isValid) {
            setErrors({ ...EMPTY_CANDIDATE_ERRORS });
            setAnswerErrors({});
          } else {
            return;
          }

          if (campaign && campaign.campaign.showAdvancedForm) {
            setActiveStep(ACTIVE_STEP_ADVANCED_FORM);
          } else if (campaign && campaign.campaign.requirePassportScan) {
            setActiveStep(ACTIVE_STEP_UPLOAD_PASSPORT);
          } else {
            handleSave();
          }
        }
        break;
      case ACTIVE_STEP_ADVANCED_FORM:
        console.log("ACTIVE_STEP_ADVANCED_FORM -> Next");
        {
          const isValid = await validateInput(candidate.candidate);
          if (isValid) {
            setErrors({ ...EMPTY_CANDIDATE_ERRORS });
          } else {
            return;
          }
          if (
            campaign &&
            (campaign.campaign.requirePassportScan || isKyrgyzstanOperator)
          ) {
            setActiveStep(ACTIVE_STEP_UPLOAD_PASSPORT);
          } else {
            handleSave();
          }
        }
        break;
      case ACTIVE_STEP_UPLOAD_PASSPORT:
        handleSave();
        break;
    }
  };

  const getFinished = () => {
    const hours =
      campaign?.campaign.verificationExpiryHours || VERIFICATION_EXPIRY_HOURS;
    const verificationType = campaign?.campaign.verificationType || "email";
    const isKyrgyzstanOperator =
      campaign?.campaign.country?.code === "KG" ||
      campaign?.campaign.country?.label?.trim().toLowerCase() === "kyrgyzstan";

    if (isKyrgyzstanOperator) {
      return (
        <Box padding={"3rem"} paddingTop={"1rem"}>
          <Typography
            variant="h5"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            Жообуңуз үчүн рахмат!
          </Typography>
          <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
            Онлайн каттоодон ийгиликтүү өткөн жарандардын тизмеси жана
            тестирлөөнүн графиги Борбордун расмий сайтына —{" "}
            <Link
              href="https://www.migrant.kg"
              target="_blank"
              rel="noreferrer"
            >
              www.migrant.kg
            </Link>{" "}
            жарыяланат.
          </Typography>
          <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
            Жаңылыктарды байкап туруңуздар.
          </Typography>
          <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
            Тестирлөө өткөрүлө турган болжолдуу мөөнөт: 2026-жылдын 1-апрелинен
            27-апрелине чейин.
          </Typography>
          <Typography sx={{ marginTop: "1rem", fontWeight: 700 }}>
            Көңүл буруңуздар!
          </Typography>
          <Typography sx={{ marginTop: "0.5rem", textAlign: "left" }}>
            Тестирлөө күнү катышуучулар милдеттүү түрдө төмөнкүлөрдү алып келиши
            керек:
          </Typography>
          <ul>
            <li>жалпы жарандык паспорт;</li>
            <li>28 АКШ доллары өлчөмүндөгү төлөмдүн квитанциясы.</li>
          </ul>
        </Box>
      );
    }

    switch (verificationType) {
      case "email":
        return (
          <Box padding={"3rem"} paddingTop={"1rem"}>
            <Typography variant="h5" sx={{ marginBottom: "1rem" }}>
              <FormattedMessage
                id="register_finished_title"
                defaultMessage="Thank you for registering!"
              />
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "1rem" }}>
              <FormattedMessage
                id="register_finished_email"
                defaultMessage="We have sent you a link to confirm your email! To finish the registration process, please click on the link in the email."
              />
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "1rem" }}>
              <FormattedMessage
                id="register_finished_hint1"
                values={{ hours }}
                defaultMessage={
                  "You have {hours} hours to confirm your email address! " +
                  "Without confirmation, your registration will be deleted."
                }
              />
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "1rem" }}>
              <FormattedMessage
                id="register_finished_hint2"
                defaultMessage="If you haven't received our email, check your spam folder."
              />
            </Typography>
          </Box>
        );

      default:
        return (
          <Box padding={"3rem"} paddingTop={"1rem"}>
            <Typography
              variant="h5"
              sx={{ marginBottom: "1rem", textAlign: "center" }}
            >
              <FormattedMessage
                id="verify_registration_confirmed_title"
                defaultMessage="We want to inform you that your registration has been successful."
              />
            </Typography>
            <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
              <FormattedMessage
                id="verify_registration_confirmed_hint1"
                defaultMessage="If you are suitable for us, we will send you further instructions to your email address."
              />
            </Typography>
            <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
              <FormattedMessage
                id="verify_registration_confirmed_hint2"
                defaultMessage="Thank you for cooperation!"
              />
            </Typography>
            <Typography sx={{ textAlign: "left" }}>
              <FormattedMessage
                id="verify_registration_confirmed_hint3"
                defaultMessage="Best regards,"
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            {sponsor?.sponsor.name && (
              <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
                {sponsor?.sponsor.name}
              </Typography>
            )}
          </Box>
        );
    }
  };

  const getWelcomePage = () => {
    const sponsorName = isKyrgyzstanOperator
      ? "Центр трудоустройства граждан за рубежом"
      : sponsor?.sponsor.name;
    return (
      <Box
        sx={{
          padding: {
            xs: "1rem",
            md: "3rem",
          },
          paddingTop: "1rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          {isKyrgyzstanOperator ? (
            sponsorName
          ) : (
            <FormattedMessage
              id="register_welcome_title"
              defaultMessage="Seasonal Worker Scheme (SWS) Sponsorship through {sponsor}"
              values={{ sponsor: sponsor?.sponsor.name }}
            />
          )}
        </Typography>
        {isKyrgyzstanOperator ? (
          <>
            <Typography>
              Центр трудоустройства граждан за рубежом при Министерстве труда,
              социального обеспечения и миграции Кыргызской Республики
              продолжает реализацию Системы разрешений на трудоустройство (EPS),
              которая предоставляет гражданам Кыргызской Республики возможность
              официального трудоустройства в Республике Корея по визе типа E-9.
            </Typography>
            <Typography sx={{ marginBottom: "0.5rem", marginTop: "1rem" }}>
              Обращаем внимание, что:
            </Typography>
            <ul>
              <li>
                успешное прохождение тестирования не гарантирует получение
                трудового контракта;
              </li>
              <li>
                граждане, успешно прошедшие тестирование, включаются в единый
                реестр соискателей сроком на 2 года;
              </li>
              <li>
                отбор кандидатов в тестировании осуществляется корейской
                стороной (HRD Korea);
              </li>
              <li>
                трудовой контракт направляется исключительно работодателем из
                Республики Корея;
              </li>
              <li>
                после отбора трудовой контракт направляется кыргызской стороне в
                установленном порядке.
              </li>
            </ul>
            <Typography sx={{ marginTop: "1rem", fontWeight: 700 }}>
              Внимание!
            </Typography>
            <Typography>
              Физические, юридические лица, а также государственные органы и
              должностные лица не могут повлиять и содействовать в процесс
              трудоустройства (на сдачу документов, прохождения теста, отбор и
              получения контракта).
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              Предоставление персональных данных и согласие на их обработку
              является обязательным условием участия в программе.
            </Typography>

            <Typography sx={{ marginTop: "1.5rem" }}>
              Кыргыз Республикасынын Эмгек, социалдык камсыздоо жана миграция
              министрлигине караштуу Жарандарды чет өлкөдө ишке орноштуруу
              борбору ишке орноштурууга уруксат берүү программасын (EPS) ишке
              ашырууну улантууда. Бул система Кыргыз Республикасынын жарандарына
              E-9 жумуш виза аркылуу Корея Республикасында расмий ишке орношууга
              мүмкүнчүлүк берет.
            </Typography>
            <Typography sx={{ marginBottom: "0.5rem", marginTop: "1rem" }}>
              Төмөнкүлөргө көңүл буруу зарыл:
            </Typography>
            <ul>
              <li>
                тестирлөөдөн ийгиликтүү өтүү эмгек келишимин алууну кепилдик
                бербейт;
              </li>
              <li>
                тестирлөөдөн ийгиликтүү өткөн жарандар 2 жылдык мөөнөткө
                бирдиктүү иш издөөчүлөр реестрине киргизилет;
              </li>
              <li>
                тестирлөөдө талапкерлерди тандоо корей тарап (HRD Korea)
                тарабынан жүргүзүлөт;
              </li>
              <li>
                эмгек келишими Корея Республикасындагы иш берүүчүлөр тарабынан
                гана жөнөтүлөт;
              </li>
              <li>
                тандоодон кийин эмгек келишими белгиленген тартипте Борборго
                жөнөтүлөт.
              </li>
            </ul>
            <Typography sx={{ marginTop: "1rem", fontWeight: 700 }}>
              Көңүл буруңуз!
            </Typography>
            <Typography>
              Жеке жана юридикалык жактар, ошондой эле мамлекеттик органдар жана
              кызмат адамдары ишке орноштуруу процессине (документтерди
              тапшыруу, тесттен өтүү, талапкерлерди тандоо жана эмгек келишимин
              алуу) кийлигишпейт жана көмөк көрсөтө алышпайт.
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              Жеке маалыматтарды берүү жана аларды иштеп чыгууга макулдук берүү
              программага катышуунун милдеттүү шарты болуп саналат.
            </Typography>
          </>
        ) : (
          <>
            <Typography>
              <FormattedMessage
                id="register_welcome_hint1"
                defaultMessage="Welcome to the {sponsor} registration process. Here, you can apply for a SWS placement in the UK."
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            <Typography>
              <FormattedMessage
                id="register_welcome_hint1_2"
                defaultMessage="Please register only once using the email address you intend to use for the Seasonal Workers Scheme. Do not create multiple registrations or use different email addresses. Any duplicate registrations will be disqualified."
              />
            </Typography>
            <Typography sx={{ marginBottom: "1rem", marginTop: "1rem" }}>
              <FormattedMessage
                id="register_welcome_hint2"
                defaultMessage="You will be required to provide the following initial details in order for us to process your application:"
              />
            </Typography>
            <ul>
              <li>
                <FormattedMessage
                  id="register_welcome_hint2_1"
                  defaultMessage="Your full name"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_welcome_hint2_2"
                  defaultMessage="Passport number- if you have one"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_welcome_hint2_3"
                  defaultMessage="Email address- this must be a GMAIL account"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_welcome_hint2_4"
                  defaultMessage="Telephone number registered with WhatsApp"
                />
              </li>
            </ul>
            <Typography sx={{ marginBottom: "1rem", marginTop: "1rem" }}>
              <FormattedMessage
                id="register_welcome_hint3"
                defaultMessage="For the purpose of managing recruitment, if you are invited to attend an interview, {sponsor} will record all interviews."
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              <FormattedMessage
                id="register_welcome_hint4"
                defaultMessage="The provision of personal details and recording all interviews is essential to proceed in the recruitment and placement process."
              />
            </Typography>
          </>
        )}

        <Grid container spacing={2} marginBottom={"1rem"}>
          {/* ---------- Privacy Policy ----------*/}
          {!isKyrgyzstanOperator ? (
            <Grid item xs={12}>
              <Box>
                <Typography sx={{ marginTop: "1rem" }}>
                  <FormattedMessage
                    id="privacy_hint1"
                    defaultMessage="Please read our Privacy Policy, which explains why and how we process information:"
                  />
                </Typography>
                <Typography>
                  <Link
                    href="https://www.hopslaboursolutions.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.hopslaboursolutions.com/privacy-policy
                  </Link>
                </Typography>
              </Box>
            </Grid>
          ) : (
            <div style={{ marginTop: "1rem" }}>&nbsp;</div>
          )}
          <Grid item xs={12}>
            <FormControl error={!!errors.privacy_policy_accepted}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="privacy_policy"
                    size="medium"
                    checkedIcon={<Circle />}
                    icon={<CircleOutlined />}
                    onChange={handleInputChange}
                  />
                }
                label={
                  <FormattedMessage
                    id="privacy_policy"
                    defaultMessage={
                      "I have read and agree with the Privacy Policy of {sponsor}"
                    }
                    values={{ sponsor: sponsorName }}
                  />
                }
              />
              <FormHelperText>
                {errors.privacy_policy_accepted && (
                  <FormattedMessage
                    id="privacy_policy_error"
                    defaultMessage={errors.privacy_policy_accepted}
                  />
                )}
              </FormHelperText>
            </FormControl>
            <FormControl error={!!errors.personal_data_processing}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="personal_data_processing"
                    size="medium"
                    checkedIcon={<Circle />}
                    icon={<CircleOutlined />}
                    onChange={handleInputChange}
                  />
                }
                label={
                  <FormattedMessage
                    id="personal_data_processing"
                    defaultMessage={
                      "I consent to providing my personal details and my interview being recorded."
                    }
                  />
                }
              />
              <FormHelperText>
                {errors.personal_data_processing && (
                  <FormattedMessage
                    id="personal_data_processing_error"
                    defaultMessage={errors.personal_data_processing}
                  />
                )}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button variant="contained" onClick={handleNext}>
                <FormattedMessage id="button_next" defaultMessage={"Next"} />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getDisclaimer = () => {
    return (
      <Box
        sx={{
          padding: {
            xs: "1rem",
            md: "3rem",
          },
          paddingTop: "1rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_disclaimer_title"
            defaultMessage="Fees & Acts for placements"
          />
        </Typography>

        <Grid
          container
          marginTop={"2rem"}
          marginBottom={"1rem"}
          gap={3}
          alignItems="center"
        >
          <Grid
            item
            xs={12}
            md={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <WarningIcon color="error" sx={{ fontSize: "4rem" }} />
          </Grid>
          <Grid item xs={12} md={10} sx={{ justifyContent: "center" }}>
            <Typography sx={{ color: "error.main", fontWeight: "bold" }}>
              <FormattedMessage
                id="register_disclaimer_text"
                defaultMessage={register_disclaimer_text}
              />
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              <FormattedMessage
                id="register_disclaimer_hint1"
                defaultMessage="The following are the only costs you will need to cover:"
              />
            </Typography>
            <ul>
              <li>
                <FormattedMessage
                  id="register_disclaimer_hint1_1"
                  defaultMessage="Passport"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_disclaimer_hint1_2"
                  defaultMessage="Visa"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_disclaimer_hint1_3"
                  defaultMessage="VFS / TLS"
                />
              </li>
              <li>
                <FormattedMessage
                  id="register_disclaimer_hint1_4"
                  defaultMessage="Travel"
                />
              </li>
            </ul>
            <Typography sx={{ marginTop: "1rem" }}>
              <FormattedMessage
                id="register_disclaimer_hint_2"
                defaultMessage={
                  "Please be aware of scams, false or misleading information and third parties acting in bad faith. " +
                  "You must only trust information provided directly to you by {sponsor}."
                }
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              <FormattedMessage
                id="register_disclaimer_hint_3"
                defaultMessage={
                  "Only trust information provided directly to you by {sponsor}."
                }
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              <FormattedMessage
                id="register_disclaimer_hint_4"
                defaultMessage={
                  "If you or someone you know has been asked to pay money or do anything in return for registration " +
                  "and / or for of placement, please report this to {sponsor} directly."
                }
                values={{ sponsor: sponsor?.sponsor.name }}
              />
            </Typography>
            <Box>
              <Typography sx={{ marginTop: "1rem" }}>
                <FormattedMessage
                  id="register_disclaimer_hint_5"
                  defaultMessage="For more information and {sponsor} contact details, please refer to our website:"
                  values={{ sponsor: sponsor?.sponsor.name }}
                />
              </Typography>
              <Typography>
                <Link
                  href="https://www.hopslaboursolutions.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://www.hopslaboursolutions.com/
                </Link>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button variant="contained" onClick={handleNext}>
                <FormattedMessage id="button_next" defaultMessage={"Next"} />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getGoogleVerification = () => {
    return (
      <Box
        sx={{
          padding: {
            xs: "1rem",
            md: "3rem",
          },
          paddingTop: "1rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_google_required_title"
            defaultMessage="Google sign-in required"
          />
        </Typography>
        <Typography sx={{ marginBottom: "1.5rem", textAlign: "center" }}>
          <FormattedMessage
            id="register_google_required_text"
            defaultMessage="To proceed, please sign in with your Google account so we can verify your email address."
          />
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              {!isGoogleVerified ? (
                <div ref={googleBtnRef} />
              ) : (
                // show the verified chip and a reset action if needed
                <Chip
                  color="success"
                  label={
                    <FormattedMessage
                      id="google_verified_summary"
                      defaultMessage="Using Google verified email: {email}"
                      values={{ email: candidate.candidate.email }}
                    />
                  }
                />
              )}
              {googleVerificationError && (
                <Typography color="error" variant="body2" textAlign="center">
                  {googleVerificationError}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isGoogleVerified}
              >
                <FormattedMessage id="button_next" defaultMessage="Next" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getForm = () => {
    return (
      <Box
        sx={{
          padding: {
            xs: "0rem",
            md: "3rem",
          },
          paddingTop: "1rem",
        }}
      >
        {isGoogleVerificationCampaign && isGoogleVerified && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Chip
              color="success"
              label={
                <FormattedMessage
                  id="google_verified_summary"
                  defaultMessage="Using Google verified email: {email}"
                  values={{ email: candidate.candidate.email }}
                />
              }
            />
          </Box>
        )}
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          {isKyrgyzstanOperator ? (
            "Центр трудоустройства граждан за рубежом"
          ) : (
            <FormattedMessage
              id="register_register_title"
              defaultMessage="Seasonal Worker Scheme (SWS) Sponsorship through {sponsor}"
              values={{ sponsor: sponsor?.sponsor.name }}
            />
          )}
        </Typography>
        <Typography sx={{ marginBottom: "1rem" }}>
          <FormattedMessage
            id="register_register_hint1"
            defaultMessage="You can register with your names, email address and telephone number."
          />
        </Typography>

        {/* ---------- Data Form ---------- */}
        <Grid container spacing={2} marginTop={"1rem"} marginBottom={"1rem"}>
          {/* <Grid item xs={12}>
            <Button variant="contained" onClick={handleLoadPassport}>
              load passport
            </Button>
          </Grid> */}

          <Grid item xs={12} md={6}>
            <CustomTextField
              error={!!errors.surname}
              helperText={errors.surname}
              autoFocus
              margin="dense"
              name="surname"
              label="Surname"
              type="text"
              fullWidth
              required
              value={candidate.candidate.surname || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              error={!!errors.name}
              helperText={errors.name}
              margin="dense"
              name="name"
              label="Name"
              type="text"
              fullWidth
              required
              value={candidate.candidate.name || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              error={!!errors.email}
              helperText={errors.email}
              margin="dense"
              name="email"
              label="Email Address"
              type="email"
              fullWidth
              required
              value={candidate.candidate.email || ""}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={isGoogleVerificationCampaign && isGoogleVerified}
            />
            {emailSuggestion && (
              <Chip
                color="warning"
                label={
                  <FormattedMessage
                    id="email_suggestion"
                    defaultMessage={`Press to use {email} instead`}
                    values={{ email: emailSuggestion }}
                  />
                }
                size={"medium"}
                variant={"outlined"}
                onClick={() => {
                  setCandidate({
                    ...candidate,
                    candidate: {
                      ...candidate.candidate,
                      email: emailSuggestion,
                    },
                  });
                  setEmailSuggestion("");
                }}
                onDelete={() => {
                  setEmailSuggestion("");
                }}
                sx={{
                  width: "100%",
                  marginTop: "0.5rem",
                  "& .MuiChip-deleteIcon": {
                    marginLeft: "auto",
                  },
                  height: "auto",
                  minHeight: "2rem",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                  },
                }}
              />
            )}
            {campaign?.campaign.emailProviderUrl &&
              !isGoogleVerificationCampaign && (
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="warning" />
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: "0.5rem",
                      fontSize: "0.8rem",
                      color: errors.emailDomain ? "error.main" : "inherit",
                    }}
                  >
                    <FormattedMessage
                      id="email_provider_url"
                      defaultMessage="Please, use the email address from the email provider {emailProviderUrl}"
                      values={{
                        emailProviderUrl: campaign.campaign.emailProviderUrl,
                      }}
                    />
                  </Typography>
                </Box>
              )}
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              name="phone"
              label="Phone number"
              error={!!errors.phone}
              helperText={errors.phone}
              type="tel"
              value={candidate.candidate.phone || ""}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              name="birth_date"
              label="Date of birth"
              error={!!errors.birth_date}
              helperText={errors.birth_date}
              type="date"
              value={candidate.candidate.birth_date || ""}
              onChange={handleInputChange}
              onBlur={isKyrgyzstanOperator ? handleBirthDateBlur : undefined}
              fullWidth
              required
            />
          </Grid>
          {campaign?.campaign.includeRegion && regions && (
            <Grid item xs={12} md={6}>
              <CustomTextField
                error={!!errors.region}
                helperText={errors.region}
                margin="dense"
                name="region"
                fullWidth
                select
                value={candidate.candidate.region?.code || ""}
                onChange={handleRegionChange}
                label="Region"
              >
                <MenuItem value="no_region">
                  <FormattedMessage
                    id="no_region"
                    defaultMessage="Select a region"
                  />
                </MenuItem>
                {regions.regions.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {region.label}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <CustomTextField
              name="gender"
              label="Gender"
              select
              error={!!errors.gender}
              helperText={errors.gender}
              value={
                candidate.candidate.gender ? candidate.candidate.gender : ""
              }
              onChange={handleInputChange}
              sx={{ width: "160px" }}
              required
            >
              {isKyrgyzstanOperator
                ? [
                    <MenuItem key="gender_m" value="M">
                      Male(M)
                    </MenuItem>,
                    <MenuItem key="gender_f" value="F">
                      Female(F)
                    </MenuItem>,
                  ]
                : [
                    <MenuItem key="gender_m" value="M">
                      <FormattedMessage id="sex_male" defaultMessage="Male" />
                    </MenuItem>,
                    <MenuItem key="gender_f" value="F">
                      <FormattedMessage
                        id="sex_female"
                        defaultMessage="Female"
                      />
                    </MenuItem>,
                    <MenuItem key="gender_d" value="D">
                      <FormattedMessage
                        id="sex_diverse"
                        defaultMessage="Diverse"
                      />
                    </MenuItem>,
                  ]}
            </CustomTextField>
          </Grid>

          {/* ---------- Passport ---------- */}
          <Grid item xs={12} md={6}>
            <RadioYesNo
              id="has_passport"
              value={candidate.candidate.has_passport}
              label="Do you have a travel passport?"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              error={!!errors.passportId}
              helperText={errors.passportId}
              margin="dense"
              name="passportId"
              label="Passport Number"
              type="text"
              fullWidth
              disabled={!candidate.candidate.has_passport}
              value={candidate.candidate.passportId || ""}
              onChange={handleInputChange}
            />
          </Grid>
          {campaign?.campaign.includeUniversity && (
            <Grid item xs={12} md={6}>
              <CustomTextField
                name="university"
                label="University"
                select
                error={!!errors.university}
                helperText={errors.university}
                value={
                  candidate.candidate.university
                    ? candidate.candidate.university
                    : "no_university"
                }
                onChange={handleInputChange}
                fullWidth
                required
              >
                <MenuItem value="no_university">
                  <FormattedMessage
                    id="no_university"
                    defaultMessage="Select a university"
                  />
                </MenuItem>
                {UNIVERSITY_QUESTIONS.sort().map((university) => (
                  <MenuItem key={university} value={university}>
                    {university}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>
          )}

          {/* ---------- Travel Companion ---------- */}
          {campaign?.campaign.askForSpouseApplication && (
            <>
              <Grid item xs={12}>
                <FormControl>
                  <FormLabel id="travelCompanion_label">
                    <FormattedMessage
                      id="travel_companion_label"
                      defaultMessage="Travelling"
                    />
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="travelCompanion_label"
                    id="travelCompanion"
                    name="travelCompanion"
                    value={candidate.candidate.travelCompanion || "alone"}
                    onChange={handleInputChange}
                  >
                    <FormControlLabel
                      value="alone"
                      control={<Radio />}
                      label={
                        <FormattedMessage
                          id="travel_companion_alone"
                          defaultMessage="Alone"
                        />
                      }
                    />
                    <FormControlLabel
                      value="spouse"
                      control={<Radio />}
                      label={
                        <FormattedMessage
                          id="travel_companion_spouse"
                          defaultMessage="With spouse"
                        />
                      }
                    />
                    <FormControlLabel
                      value="relative_friend"
                      control={<Radio />}
                      label={
                        <FormattedMessage
                          id="travel_companion_relative_friend"
                          defaultMessage="With relative / friend"
                        />
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {(candidate.candidate.travelCompanion === "spouse" ||
                candidate.candidate.travelCompanion === "relative_friend") && (
                <>
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      translated={false}
                      error={!!errors.spouseName}
                      helperText={errors.spouseName}
                      margin="dense"
                      name="spouseName"
                      label={
                        candidate.candidate.travelCompanion === "spouse" ? (
                          <FormattedMessage
                            id="spouse_name"
                            defaultMessage="Spouse Name"
                          />
                        ) : (
                          <FormattedMessage
                            id="companion_name"
                            defaultMessage="Companion Name"
                          />
                        )
                      }
                      type="text"
                      fullWidth
                      required
                      value={candidate.candidate.spouseName || ""}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      translated={false}
                      error={!!errors.spouseSurname}
                      helperText={errors.spouseSurname}
                      margin="dense"
                      name="spouseSurname"
                      label={
                        candidate.candidate.travelCompanion === "spouse" ? (
                          <FormattedMessage
                            id="spouse_surname"
                            defaultMessage="Spouse Surname"
                          />
                        ) : (
                          <FormattedMessage
                            id="companion_surname"
                            defaultMessage="Companion Surname"
                          />
                        )
                      }
                      type="text"
                      fullWidth
                      required
                      value={candidate.candidate.spouseSurname || ""}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField
                      translated={false}
                      error={!!errors.spouseEmail}
                      helperText={errors.spouseEmail}
                      margin="dense"
                      name="spouseEmail"
                      label={
                        candidate.candidate.travelCompanion === "spouse" ? (
                          <FormattedMessage
                            id="spouse_email"
                            defaultMessage="Spouse Email Address"
                          />
                        ) : (
                          <FormattedMessage
                            id="companion_email"
                            defaultMessage="Companion Email Address"
                          />
                        )
                      }
                      type="email"
                      fullWidth
                      required
                      value={candidate.candidate.spouseEmail || ""}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  {candidate.candidate.travelCompanion ===
                    "relative_friend" && (
                    <>
                      <Grid item xs={12} md={4}>
                        <CustomTextField
                          translated={false}
                          name="companionGender"
                          label={
                            <FormattedMessage
                              id="companion_gender"
                              defaultMessage="Companion Gender"
                            />
                          }
                          select
                          error={!!errors.companionGender}
                          helperText={errors.companionGender}
                          value={candidate.candidate.companionGender || ""}
                          onChange={handleInputChange}
                          required
                          fullWidth
                        >
                          <MenuItem value="M">
                            <FormattedMessage
                              id="sex_male"
                              defaultMessage="Male"
                            />
                          </MenuItem>
                          <MenuItem value="F">
                            <FormattedMessage
                              id="sex_female"
                              defaultMessage="Female"
                            />
                          </MenuItem>
                          <MenuItem value="D">
                            <FormattedMessage
                              id="sex_diverse"
                              defaultMessage="Diverse"
                            />
                          </MenuItem>
                        </CustomTextField>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <CustomTextField
                          translated={false}
                          name="companionRelationType"
                          label={
                            <FormattedMessage
                              id="companion_relation_type"
                              defaultMessage="Companion Relationship Type"
                            />
                          }
                          select
                          error={!!errors.companionRelationType}
                          helperText={errors.companionRelationType}
                          value={
                            candidate.candidate.companionRelationType || ""
                          }
                          onChange={handleInputChange}
                          fullWidth
                          required
                        >
                          <MenuItem value="sibling">
                            <FormattedMessage
                              id="companion_relation_sibling"
                              defaultMessage="Sibling"
                            />
                          </MenuItem>
                          <MenuItem value="parent">
                            <FormattedMessage
                              id="companion_relation_parent"
                              defaultMessage="Parent"
                            />
                          </MenuItem>
                          <MenuItem value="other_relative">
                            <FormattedMessage
                              id="companion_relation_other_relative"
                              defaultMessage="Other relative"
                            />
                          </MenuItem>
                          <MenuItem value="friend">
                            <FormattedMessage
                              id="companion_relation_friend"
                              defaultMessage="Friend"
                            />
                          </MenuItem>
                        </CustomTextField>
                      </Grid>
                    </>
                  )}

                  {candidate.candidate.travelCompanion === "spouse" && (
                    <Grid item xs={12} md={12}>
                      <RadioYesNo
                        id="travelIfSpouseUnsuccessful"
                        value={candidate.candidate.travelIfSpouseUnsuccessful}
                        label="If your spouse's application is unsuccessful, are you willing to travel to Great Britain on your own?"
                        onChange={handleInputChange}
                      />
                    </Grid>
                  )}
                </>
              )}
            </>
          )}

          {campaign?.campaign.showCurrentLocation && (
            <>
              <Grid item xs={12} md={6}>
                <SelectCountryTranslated
                  id="currentLocation"
                  label="Where are you currently located?"
                  value={
                    candidate.candidate.currentLocation || {
                      code: "",
                      label: "",
                    }
                  }
                  onChange={(_, value) => {
                    const newCandidate = {
                      ...candidate,
                      candidate: {
                        ...candidate.candidate,
                        currentLocation: value,
                      },
                    };
                    setCandidate(newCandidate);
                  }}
                  error={!!errors.currentLocation}
                  helperText={errors.currentLocation}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RadioYesNo
                  id="canAttendInterview"
                  value={candidate.candidate.canAttendInterview}
                  label="Will you be able to attend a presentation and an interview if you are invited within the next 3 months?"
                  onChange={handleInputChange}
                  error={!!errors.canAttendInterview}
                  helperText={errors.canAttendInterview}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center">
              {!(
                campaign?.campaign.showAdvancedForm ||
                campaign?.campaign.requirePassportScan
              ) && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isGoogleVerificationCampaign && !isGoogleVerified}
                >
                  <FormattedMessage
                    id="button_register"
                    defaultMessage={"Register"}
                  />
                </Button>
              )}
              {campaign &&
                (campaign.campaign.showAdvancedForm ||
                  campaign.campaign.requirePassportScan) && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isGoogleVerificationCampaign && !isGoogleVerified}
                  >
                    <FormattedMessage
                      id="button_next"
                      defaultMessage={"Next"}
                    />
                  </Button>
                )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getAdvancedForm = () => {
    return (
      <Box
        sx={{
          padding: {
            xs: "0rem",
            md: "3rem",
          },
          paddingTop: "1rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          {isKyrgyzstanOperator ? (
            "Центр трудоустройства граждан за рубежом"
          ) : (
            <FormattedMessage
              id="register_register_title"
              defaultMessage="Seasonal Worker Scheme (SWS) Sponsorship through {sponsor}"
              values={{ sponsor: sponsor?.sponsor.name }}
            />
          )}
        </Typography>
        {/* <Typography sx={{ marginBottom: "1rem" }}>
          <FormattedMessage
            id="register_register_hint1"
            defaultMessage="You can register with your names, email address and telephone number."
          />
        </Typography> */}

        {/* ---------- Data Form ---------- */}
        <Grid container spacing={2} marginTop={"1rem"} marginBottom={"1rem"}>
          {isKyrgyzstanOperator &&
            KYRGYZ_QUESTIONS.map((question) => (
              <Grid
                item
                xs={12}
                md={(question.mdSize || 1) * 6}
                key={"ge_" + question.id}
              >
                <QuestionComponent
                  question={question}
                  error={!!answerErrors[question.id]}
                  helperText={answerErrors[question.id]}
                  value={getQuestionValue(question.id)}
                  onChange={handleAnswerChange}
                  translated={true}
                />
              </Grid>
            ))}
          {!isKyrgyzstanOperator &&
            campaign?.campaign.language === "ru" &&
            RUSSIAN_QUESTIONS.map((question) => (
              <Grid
                item
                xs={12}
                md={(question.mdSize || 1) * 6}
                key={"ge_" + question.id}
              >
                <QuestionComponent
                  question={question}
                  error={!!answerErrors[question.id]}
                  helperText={answerErrors[question.id]}
                  value={getQuestionValue(question.id)}
                  onChange={handleAnswerChange}
                  translated={true}
                />
              </Grid>
            ))}
          {!isKyrgyzstanOperator &&
            ADVANCED_FORM_QUESTIONS.map((question) => (
              <Grid
                item
                xs={12}
                md={(question.mdSize || 1) * 6}
                key={"ge_" + question.id}
              >
                <QuestionComponent
                  question={question}
                  helperText={answerErrors[question.id]}
                  error={!!answerErrors[question.id]}
                  value={getQuestionValue(question.id)}
                  onChange={handleAnswerChange}
                  translated={true}
                />
              </Grid>
            ))}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center">
              {!(
                campaign?.campaign.requirePassportScan || isKyrgyzstanOperator
              ) && (
                <LoadingButton
                  loading={registerInProgress.current}
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    registerInProgress.current ||
                    (isGoogleVerificationCampaign && !isGoogleVerified)
                  }
                  sx={{ mt: 2 }}
                >
                  <FormattedMessage
                    id="button_register"
                    defaultMessage="Register"
                  />
                </LoadingButton>
              )}
              {campaign &&
                (campaign.campaign.requirePassportScan ||
                  isKyrgyzstanOperator) && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isGoogleVerificationCampaign && !isGoogleVerified}
                  >
                    <FormattedMessage
                      id="button_next"
                      defaultMessage={"Next"}
                    />
                  </Button>
                )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getUploadPassport = () => {
    if (isKyrgyzstanOperator) {
      return (
        <Box padding={{ xs: "0rem", md: "3rem" }} paddingTop={"1rem"}>
          <Typography
            variant="h5"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            <FormattedMessage
              id="register_upload_kyrgyz_title"
              defaultMessage="Документтерди жуктөңүз / Загрузите документы"
            />
          </Typography>

          {/* <Typography
            variant="body1"
            sx={{ marginBottom: "1.5rem", textAlign: "center" }}
          >
            <FormattedMessage
              id="register_upload_kyrgyz_instructions"
              defaultMessage="Please upload all 3 required files in JPEG/JPG format."
            />
          </Typography> */}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography>
              1. Ак фондогу 3,5х4,5 акыркы 6 айда түшкөн сүрөт / 3,5х4,5 фото на
              белом фоне за последние 6 месяцев в формате JPEG*
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
            >
              <FormattedMessage
                id="register_upload_kyrgyz_photo_button"
                defaultMessage="Upload photo"
              />
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,.jpeg,.jpg"
                onChange={(e) => {
                  handleJpegUpload(
                    e.target.files?.[0],
                    setKyrgyzPhotoSrc,
                    setKyrgyzPhotoExtension,
                  );
                  e.target.value = "";
                }}
              />
            </Button>
            {kyrgyzPhotoSrc && (
              <Card sx={{ maxWidth: 320 }}>
                <img
                  src={kyrgyzPhotoSrc}
                  alt="Kyrgyz photo preview"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Card>
            )}

            <Typography sx={{ marginTop: "1rem" }}>
              2. Жалпы жарандык паспорттун сүрөтү / Фото заграничного паспорта
              качественно сфотографируйте) в формате JPEG
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
            >
              <FormattedMessage
                id="register_upload_kyrgyz_passport_button"
                defaultMessage="Upload passport image"
              />
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,.jpeg,.jpg"
                onChange={(e) => {
                  handleJpegUpload(
                    e.target.files?.[0],
                    setKyrgyzPassportSrc,
                    setKyrgyzPassportExtension,
                  );
                  e.target.value = "";
                }}
              />
            </Button>
            {kyrgyzPassportSrc && (
              <Card sx={{ maxWidth: 320 }}>
                <img
                  src={kyrgyzPassportSrc}
                  alt="Kyrgyz passport preview"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Card>
            )}

            <Typography sx={{ marginTop: "1rem" }}>
              3. 28 $ АКШ долларын төлөгөндүгү тууралуу квитанция. МИЛДЕТТҮҮ!
              Сүрөткө тартсаңыз болот / Квитанция об оплате 28 долларах США
              ОБЯЗАТЕЛЬНО! (Можете сфотографировать)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
            >
              <FormattedMessage
                id="register_upload_kyrgyz_receipt_button"
                defaultMessage="Upload payment receipt"
              />
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,.jpeg,.jpg"
                onChange={(e) => {
                  handleJpegUpload(
                    e.target.files?.[0],
                    setKyrgyzReceiptSrc,
                    setKyrgyzReceiptExtension,
                  );
                  e.target.value = "";
                }}
              />
            </Button>
            {kyrgyzReceiptSrc && (
              <Card sx={{ maxWidth: 320 }}>
                <img
                  src={kyrgyzReceiptSrc}
                  alt="Kyrgyz receipt preview"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Card>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1.5rem",
            }}
          >
            <LoadingButton
              loading={registerInProgress.current}
              variant="contained"
              onClick={handleSave}
              disabled={
                !kyrgyzPhotoSrc ||
                !kyrgyzPassportSrc ||
                !kyrgyzReceiptSrc ||
                registerInProgress.current ||
                (isGoogleVerificationCampaign && !isGoogleVerified)
              }
            >
              <FormattedMessage
                id="button_register"
                defaultMessage="Register"
              />
            </LoadingButton>
          </Box>
        </Box>
      );
    }

    return (
      <Box padding={{ xs: "0rem", md: "3rem" }} paddingTop={"1rem"}>
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_upload_passport_title"
            defaultMessage="Upload Image"
          />
        </Typography>

        <Typography
          variant="body1"
          sx={{ marginBottom: "2rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_upload_passport_instructions"
            defaultMessage="Please upload a clear photo of yourself ин а пасспорт формат on a light background."
          />
        </Typography>

        <Typography
          variant="body1"
          sx={{ marginBottom: "2rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_upload_passport_instructions2"
            defaultMessage="Upload a jpeg or jpg image maximum 5MB."
          />
        </Typography>

        {imageSrc && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Card
              sx={{
                maxWidth: 400,
                width: "100%",
                position: "relative",
              }}
            >
              <img
                src={imageSrc}
                alt="Passport preview"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Card>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
          >
            <FormattedMessage
              id="register_upload_passport_button"
              defaultMessage="Choose Passport Image"
            />
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/jpg"
              onChange={(e) => {
                handleJpegUpload(
                  e.target.files?.[0],
                  setImageSrc,
                  setImageFileExtension,
                );
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            />
          </Button>

          {/* {imageSrc && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setImageSrc(null);
                setImageFileExtension("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              <FormattedMessage
                id="register_upload_passport_remove"
                defaultMessage="Remove Image"
              />
            </Button>
          )} */}

          <LoadingButton
            loading={registerInProgress.current}
            variant="contained"
            onClick={handleSave}
            disabled={
              !imageSrc ||
              registerInProgress.current ||
              (isGoogleVerificationCampaign && !isGoogleVerified)
            }
            sx={{ mt: 2 }}
          >
            <FormattedMessage id="button_register" defaultMessage="Register" />
          </LoadingButton>
        </Box>
      </Box>
    );
  };

  const getInvalidCampaign = () => {
    return (
      <Box padding={"3rem"} paddingTop={"1rem"}>
        <Typography
          variant="h5"
          color="error"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_campaign_invalid_title"
            defaultMessage="The regitration link you have been provided is invalid!"
          />
        </Typography>
      </Box>
    );
  };

  const getExpiredCampaign = () => {
    return (
      <Box padding={"3rem"} paddingTop={"1rem"}>
        <Typography
          variant="h5"
          color="error"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          <FormattedMessage
            id="register_campaign_expired_title"
            defaultMessage="This campaign has alread expired and does not accept registrations anymore!"
          />
        </Typography>
      </Box>
    );
  };

  const getEmpty = () => {
    return (
      <Box padding={"3rem"} paddingTop={"1rem"}>
        <Typography
          variant="h5"
          sx={{ marginBottom: "1rem", textAlign: "center" }}
        >
          {
            isKyrgyzstanOperator
              ? sponsor?.sponsor.name || ""
              : "Global Workforce Platform"
            // <FormattedMessage
            //   id="register_empty_title"
            //   defaultMessage="Seasonal work in the UK"
            // />
          }
        </Typography>
      </Box>
    );
  };

  const getActivePage = () => {
    switch (activeStep) {
      case ACTIVE_STEP_WELCOME:
        return getWelcomePage();
      case ACTIVE_STEP_DISCLAIMER:
        return getDisclaimer();
      case ACTIVE_STEP_GOOGLE:
        return getGoogleVerification();
      case ACTIVE_STEP_REGISTER_FORM:
        return getForm();
      case ACTIVE_STEP_ADVANCED_FORM:
        return getAdvancedForm();
      case ACTIVE_STEP_UPLOAD_PASSPORT:
        return getUploadPassport();
      case ACTIVE_STEP_FINISHED:
        return getFinished();
      case ACTIVE_STEP_INVALID_CAMPAIGN:
        return getInvalidCampaign();
      case ACTIVE_STEP_EXPIRED_CAMPAIGN:
        return getExpiredCampaign();
      case ACTIVE_STEP_EMPTY:
        return getEmpty();
      default:
        return getEmpty();
    }
  };

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <Container maxWidth="md" sx={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          {sponsor && sponsor.sponsor.logo && (
            <img
              src={sponsor.sponsor.logo}
              alt="operatorlogo"
              style={{ maxHeight: "200px", width: "auto" }}
            />
          )}
        </Box>
        <Card sx={{ marginTop: "1rem", padding: "1rem" }}>
          {getActivePage()}
        </Card>
      </Container>
    </IntlProvider>
  );
};
